<template>
  <div class="mb-2 mr-2 h-72">
    <o-scroll class="w-full h-full">
      <o-alert
        v-if="hasConflicts"
        show
        variant="warning"
        :title="$t('components.reservation.availability.manual.controller.alertTitle')"
        class="text-left text-sm mb-3"
      >
        <div v-for="(service, key) in conflicts" :key="key">
          <p class="font-semibold">{{ service.service.title }}</p>

          <ul class="list-disc list-inside pl-2 ml-2">
            <li v-for="conflict in service.conflicts" :key="conflict.idReservationService">
              <span v-if="conflict.reason === 'notAvailable'">Not available: {{ conflict.employee.displayName }}</span>
              <span v-if="conflict.reason === 'notAllowed'">Not allowed: {{ conflict.employee.displayName }}</span>
            </li>
          </ul>
        </div>
      </o-alert>

      <template v-if="_slot">
        <template v-for="(service, index) in _slot.services" :key="service.idReservationService">
          <div
            v-if="service.service"

            class="p-3 border bg-white rounded-md mb-2 dark:bg-gray-800"
          >
            <manual-service
              v-model="_slot.services[index]"
              v-model:service="services[index]"
              :conflicts="conflicts"
              :date="date"
              :move="move"
              @change="handleChange(index)"
            />
          </div>
        </template>
      </template>
    </o-scroll>
  </div>
</template>

<script setup lang="ts">
import ManualService from '@/components/reservation/availability/manual/Service.vue'

import {
  OAlert,
} from '@/components/elements'

import {
  CheckboxField,
} from '@/components/fields'

import { ReservationBufferTimeMode } from '@/_api/models/reservationServiceModel'

const { $date } = useNuxtApp()

interface Props {
  availabilitySlot?: Record<string, any> | null
  services: any[]
  date: Date
  startTime?: number | string
  override?: boolean
  hasConflicts?: boolean
  conflicts?: any[]
  move?: Record<string, any> | null
}

interface Emits {
  (e: 'update:services', value: any[]): void
  (e: 'update:availabilitySlot', value: any): void
  (e: 'update:override', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  availabilitySlot: null,
  startTime: 540,
  override: false,
  hasConflicts: false,
  conflicts: () => [],
  move: null,
})

const emit = defineEmits<Emits>()

const _services = computed({
  get () {
    return props.services
  },
  set (value) {
    emit('update:services', value)
  },
})

const _slot = computed({
  get () {
    return props.availabilitySlot
  },
  set (value) {
    emit('update:availabilitySlot', value)
  },
})

const _override = computed({
  get () {
    return props.override
  },
  set (value) {
    emit('update:override', value)
  },
})

watch(() => props.date, (dateVal) => {
  _slot.value!.date = dateVal
})

watch(() => _slot.value?.services, (services) => {
  if (services?.length) {
    const startTimes = services.map((service: any) => Number(service.startTime))
    const start = Math.min.apply(Math, startTimes)

    _slot.value!.start = start
  }
}, { deep: true })

watch(() => props.services, () => {
  setSlot()
}, { deep: true })

function setSlot () {
  const dateVal = props.date
  let st = props.availabilitySlot?.services?.[0]?.startTime || props.services?.[0]?.startTime || Number(props.startTime) || 540

  const services = props.services
    .map((reservationService: any) => {
      const {
        employee,
        service,
        idAssociation,
        association,
        duration,
        processing,
        finishing,
        bufferTimePre,
        bufferTimePost,
        bufferTimeMode,
        price,
        resources,
      } = reservationService

      const currentStartTime = st

      const item = {
        employee,
        service,
        idAssociation,
        association,
        duration,
        processing,
        finishing,
        bufferTimePre,
        bufferTimePost,
        bufferTimeMode,
        price,
        resources,
        startTime: currentStartTime,
        reservation: null,
        get slot () {
          const utc = $date.getStartOfDayUTC(props.date)
          return $date.timestamp(utc) + (this.startTime * 60)
        },
      }

      let bufferTimes = 0

      if (service?.bufferTimeMode === ReservationBufferTimeMode.interService) {
        bufferTimes = bufferTimePost
      }

      st = st + duration + finishing + bufferTimes

      return item
    })

  _slot.value = {
    services,
    date: $date.getStartOfDayUTC(dateVal),
    start: services?.[0].startTime,
  }
}

function handleChange (index: number) {
  if (index === 0 && _slot.value?.services.length > 1) {
    let st = _slot.value?.services?.[0]?.startTime || 540

    for (const idx in props.services) {
      const {
        service,
        duration,
        finishing,
        bufferTimePost,
      } = props.services[idx]

      let bufferTimes = 0

      if (service?.bufferTimeMode === ReservationBufferTimeMode.interService) {
        bufferTimes = bufferTimePost
      }

      _slot.value!.services[idx].startTime = st

      st = st + duration + finishing + bufferTimes
    }
  }
}

onMounted(() => {
  setSlot()
})
</script>
